import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { useFillMainViewport } from '@/hooks/useFillMainViewport';
import { PageDepthContext, usePageDepth } from './PageDepthContext';

export interface PageBodyProps {
  children: ReactNode;
  className?: string;
  /**
   * A working screen that fills the viewport and scrolls inside its own panes
   * (the records master-detail, the assistant's conversation): locks the
   * shell's `<main>` scroll and makes the body a flex column of the viewport's
   * height. Same `p-6` all round — there is no end of content to scroll clear
   * of the chat button, so no `pb-chat-safe`.
   */
  fill?: boolean;
  /**
   * The host pads the page and reserves nothing for the chat button (the
   * Bitrix24 frame's own panel around an embedded screen): no padding of its
   * own, neither `p-6` nor `pb-chat-safe` — the frame is as tall as the
   * content, and a reserve would be measured into it.
   */
  flush?: boolean;
  'data-testid'?: string;
}

/**
 * The body of a page: full width, `p-6`, no `mx-auto`/`max-w-*` (#2073,
 * program #2079). A narrow form may cap its own width, never the page's.
 * `PageHeader` goes inside it as the first child.
 *
 * No `overflow` of its own: the app shell's `<main>` is the scroll region.
 * Inside the shell nothing bounds this element's height, so an
 * `overflow-y-auto` here never scrolled — it only made this element the
 * scroll container of a `sticky` header, which then never stuck.
 */
export function PageBody({
  children,
  className,
  fill = false,
  flush = false,
  'data-testid': testId = 'page-body',
}: PageBodyProps) {
  useFillMainViewport(fill);
  const depth = usePageDepth();
  return (
    <PageDepthContext.Provider value={depth + 1}>
      <div
        data-testid={testId}
        // pb-chat-safe: the Bitrix24 chat button occupies the bottom-right corner
        // of every page, so the page ends with room to clear it.
        // See styles/chat-widget.css.
        className={cn(
          'flex-1',
          flush ? 'p-0' : 'p-6',
          // h-full, not just flex-1: between <main> and the page sits the route
          // transition's block wrapper (`h-full`), where flex-1 sizes nothing.
          fill ? 'flex h-full min-h-0 flex-col' : !flush && 'pb-chat-safe',
          className
        )}
      >
        {children}
      </div>
    </PageDepthContext.Provider>
  );
}
